import { TABLE_NOTIFICATIONS, TABLE_WORKFLOW_SNAPSHOT } from '../../constants';
import type { TABLE_NAMES, TABLE_OBSERVATIONAL_MEMORY } from '../../constants';
import type { StorageColumn } from '../../types';
import { StoreOperations } from './base';

// InMemory storage supports all tables including observational memory
type InMemoryTableNames = TABLE_NAMES | typeof TABLE_OBSERVATIONAL_MEMORY;

export class StoreOperationsInMemory extends StoreOperations {
  data: Record<InMemoryTableNames, Map<string, Record<string, any>>>;

  constructor() {
    super();
    this.data = {
      mastra_workflow_snapshot: new Map(),
      mastra_messages: new Map(),
      mastra_threads: new Map(),
      mastra_traces: new Map(),
      mastra_resources: new Map(),
      mastra_scorers: new Map(),
      mastra_ai_spans: new Map(),
      mastra_agents: new Map(),
      mastra_agent_versions: new Map(),
      mastra_observational_memory: new Map(),
      mastra_prompt_blocks: new Map(),
      mastra_prompt_block_versions: new Map(),
      mastra_scorer_definitions: new Map(),
      mastra_scorer_definition_versions: new Map(),
      mastra_mcp_clients: new Map(),
      mastra_mcp_client_versions: new Map(),
      mastra_mcp_servers: new Map(),
      mastra_mcp_server_versions: new Map(),
      mastra_workspaces: new Map(),
      mastra_workspace_versions: new Map(),
      mastra_skills: new Map(),
      mastra_skill_versions: new Map(),
      mastra_skill_blobs: new Map(),
      mastra_datasets: new Map(),
      mastra_dataset_items: new Map(),
      mastra_dataset_versions: new Map(),
      mastra_experiments: new Map(),
      mastra_experiment_results: new Map(),
      mastra_background_tasks: new Map(),
      mastra_favorites: new Map(),
      mastra_schedules: new Map(),
      mastra_schedule_triggers: new Map(),
      mastra_channel_installations: new Map(),
      mastra_channel_config: new Map(),
      mastra_tool_provider_connections: new Map(),
      mastra_notifications: new Map(),
      mastra_harness_sessions: new Map(),
      mastra_thread_state: new Map(),
    };
  }

  getDatabase() {
    return this.data;
  }

  async insert({ tableName, record }: { tableName: TABLE_NAMES; record: Record<string, any> }): Promise<void> {
    const table = this.data[tableName];
    let key = record.id;
    if (tableName === TABLE_NOTIFICATIONS && record.threadId && record.id) {
      key = `${record.threadId}\0${record.id}`;
    } else if ([TABLE_WORKFLOW_SNAPSHOT].includes(tableName) && !record.id && record.run_id) {
      key = record.workflow_name ? `${record.workflow_name}-${record.run_id}` : record.run_id;
      record.id = key;
    } else if (!record.id) {
      key = `auto-${Date.now()}-${Math.random()}`;
      record.id = key;
    }
    table.set(key, record);
  }

  async batchInsert({ tableName, records }: { tableName: TABLE_NAMES; records: Record<string, any>[] }): Promise<void> {
    const table = this.data[tableName];
    for (const record of records) {
      let key = record.id;
      if (tableName === TABLE_NOTIFICATIONS && record.threadId && record.id) {
        key = `${record.threadId}\0${record.id}`;
      } else if ([TABLE_WORKFLOW_SNAPSHOT].includes(tableName) && !record.id && record.run_id) {
        key = record.run_id;
        record.id = key;
      } else if (!record.id) {
        key = `auto-${Date.now()}-${Math.random()}`;
        record.id = key;
      }
      table.set(key, record);
    }
  }

  async load<R>({ tableName, keys }: { tableName: TABLE_NAMES; keys: Record<string, string> }): Promise<R | null> {
    const table = this.data[tableName];

    const records = Array.from(table.values());

    return records.filter(record => Object.keys(keys).every(key => record[key] === keys[key]))?.[0] as R | null;
  }

  async createTable({
    tableName,
    schema: _schema,
  }: {
    tableName: TABLE_NAMES;
    schema: Record<string, StorageColumn>;
  }): Promise<void> {
    this.data[tableName] = new Map();
  }

  async clearTable({ tableName }: { tableName: TABLE_NAMES }): Promise<void> {
    this.data[tableName].clear();
  }

  async dropTable({ tableName }: { tableName: TABLE_NAMES }): Promise<void> {
    this.data[tableName].clear();
  }

  async alterTable({
    tableName: _tableName,
    schema: _schema,
  }: {
    tableName: TABLE_NAMES;
    schema: Record<string, StorageColumn>;
    ifNotExists: string[];
  }): Promise<void> {}

  async hasColumn(_table: string, _column: string): Promise<boolean> {
    return true;
  }
}
